import BigNumber from 'bignumber.js';

import { CURRENCIES, TOKENS, upperCaseNetworkType } from 'features/bill/lib';
import { MoneyMovementOperationTypes } from './MoneyMovementOperationTypes';

export const PayoutTypes = {
  CryptoTransfer: 'cryptoTransfer',
  BankTransfer: 'bankTransfer',
};

class PayoutDetails {
  private readonly _amount: BigNumber;
  private readonly _currency: string;
  private readonly _type: string;
  private _sourceCurrency?: string;
  private _sourceNetwork?: string;

  constructor(
    amount: string,
    currency: string,
    type: string,
    sourceCurrency?: string,
    sourceNetwork?: string
  ) {
    this._amount = new BigNumber(amount);
    this._currency = currency;
    this._type = type;
    this._sourceCurrency = sourceCurrency;
    this._sourceNetwork = sourceNetwork;
  }

  get type(): string {
    return this._type;
  }

  get currency(): string {
    return this._currency;
  }

  get sourceCurrency(): string | undefined {
    return this._sourceCurrency;
  }

  get amount(): BigNumber {
    return this._amount;
  }

  get amountStr(): string {
    return this._amount.toString();
  }

  get sourceNetwork(): string {
    return this._sourceNetwork;
  }

  setSourceNetwork(network: string) {
    this._sourceNetwork = network;
  }

  setSourceCurrency(currency: string) {
    this._sourceCurrency = currency;
  }

  get amountNumber(): number {
    return this._amount.toNumber();
  }

  inferOperationType(): MoneyMovementOperationTypes | null {
    throw new Error('Not');
  }

  hasSource(): boolean {
    return !!this.sourceNetwork && !!this.sourceCurrency;
  }
}

export class CryptoPayoutDetails extends PayoutDetails {
  private readonly _network: string;
  private readonly _walletAddress: string;

  constructor(
    amount: string,
    currency: string,
    destinationDetails: {
      network?: string;
      walletAddress?: string;
    } = {},
    sourceDetails: {
      network?: string;
      currency?: string;
    } = {}
  ) {
    super(
      amount,
      currency,
      PayoutTypes.CryptoTransfer,
      sourceDetails.currency,
      sourceDetails.network
    );
    this._network = destinationDetails.network;
    this._walletAddress = destinationDetails.walletAddress;
  }

  get network(): string {
    return this._network;
  }

  get walletAddress(): string {
    return this._walletAddress;
  }

  inferOperationType(): MoneyMovementOperationTypes | null {
    if (!this.hasSource()) {
      return null;
    }

    if (
      this.sourceNetwork?.toLowerCase() === 'fiat' &&
      this.network?.toLowerCase() !== 'fiat'
    ) {
      return MoneyMovementOperationTypes.Onramp;
    }

    if (this.sourceNetwork !== this.network) {
      return MoneyMovementOperationTypes.CrossChainTransfer;
    }

    return MoneyMovementOperationTypes.OnChainTransfer;
  }
}

export class BankTransferPayoutDetails extends PayoutDetails {
  private readonly _accountNumber: string;
  private readonly _accountBeneficiary: string;
  private readonly _bankBIC: string;
  private readonly _accountIBAN: string;
  private readonly _accountACHRoutingNumber: string;
  private readonly _bankAccountBeneficiaryType: string;
  private readonly _bankName: string;

  constructor(
    amount: string,
    currency: string,
    destinationDetails: {
      accountNumber?: string;
      accountBeneficiary?: string;
      bankBIC?: string;
      accountIBAN?: string;
      accountACHRoutingNumber?: string;
      bankAccountBeneficiaryType?: string;
      bankName?: string;
    } = {},
    sourceDetails: {
      network?: string;
      currency?: string;
    } = {}
  ) {
    super(
      amount,
      currency,
      PayoutTypes.BankTransfer,
      sourceDetails.currency,
      sourceDetails.network
    );
    this._accountNumber = destinationDetails.accountNumber;
    this._accountBeneficiary = destinationDetails.accountBeneficiary;
    this._bankBIC = destinationDetails.bankBIC;
    this._accountIBAN = destinationDetails.accountIBAN;
    this._accountACHRoutingNumber = destinationDetails.accountACHRoutingNumber;
    this._bankAccountBeneficiaryType =
      destinationDetails.bankAccountBeneficiaryType;
    this._bankName = destinationDetails.bankName;
  }

  get accountNumber() {
    return this._accountNumber;
  }

  get accountBeneficiary() {
    return this._accountBeneficiary;
  }

  get bankBIC() {
    return this._bankBIC;
  }

  get accountIBAN() {
    return this._accountIBAN;
  }

  get accountACHRoutingNumber() {
    return this._accountACHRoutingNumber;
  }

  get bankAccountBeneficiaryType() {
    return this._bankAccountBeneficiaryType;
  }

  get bankName() {
    return this._bankName;
  }

  inferOperationType(): MoneyMovementOperationTypes | null {
    return MoneyMovementOperationTypes.Offramp;
  }
}

export const createPayoutDetailsFromBill = (bill: Record<string, any>) => {
  if (!bill) {
    return null;
  }

  if (CURRENCIES.includes(bill.token)) {
    return new BankTransferPayoutDetails(
      bill.amount,
      bill.token,
      bill?.invoice?.paymentDetails?.bankAccount
    );
  }

  if (TOKENS.includes(bill.token)) {
    return new CryptoPayoutDetails(bill.amount, bill.token, {
      network: bill.network,
      walletAddress: bill.walletAddress,
    });
  }

  return null;
};

export const createPayoutDetailsFromTransfer = (
  transfer: Record<string, any>
) => {
  if (!transfer) {
    return null;
  }

  if (CURRENCIES.includes(transfer.destinationCurrency)) {
    return new BankTransferPayoutDetails(
      transfer.transferAmount,
      transfer.destinationCurrency,
      transfer?.destinationPaymentDetails,
      {
        currency: transfer.sourceCurrency,
        network: transfer.sourceNetwork,
      }
    );
  }

  if (TOKENS.includes(transfer.destinationCurrency)) {
    return new CryptoPayoutDetails(
      transfer.transferAmount,
      transfer.destinationCurrency,
      {
        network: upperCaseNetworkType(transfer.destinationNetwork),
        walletAddress: transfer.destinationPaymentDetails.address,
      },
      {
        network: transfer.sourceNetwork,
        currency: transfer.sourceCurrency,
      }
    );
  }

  return null;
};

export const createPayoutDetailsFromExpense = (
  expense: Record<string, any>
) => {
  if (!expense) {
    return null;
  }

  if (CURRENCIES.includes(expense.paymentCurrency)) {
    return new BankTransferPayoutDetails(
      expense.paymentAmount,
      expense.paymentCurrency,
      expense?.recipientPaymentDetails?.bankAccount
    );
  }

  if (TOKENS.includes(expense.paymentCurrency)) {
    return new CryptoPayoutDetails(
      expense.paymentAmount,
      expense.paymentCurrency,
      {
        network: upperCaseNetworkType(expense.paymentNetwork),
        walletAddress: expense?.recipientPaymentDetails?.cryptoWallet?.address,
      }
    );
  }

  return null;
};

export const createCryptoPayoutDetails = (
  amount: string,
  token: string,
  network: string,
  address: string
) => {
  return new CryptoPayoutDetails(amount, token, {
    network,
    walletAddress: address,
  });
};
